import { type DragEvent, type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';
import type { Media } from '../api/types';
import { ACCEPTED_IMAGES, ACCEPTED_PDF, uploadImage, uploadMany, uploadPdf } from '../api/upload';
import { errorMessage } from '../api/client';
import { useT } from '../i18n';
import { IconDownload, IconFile, IconImage, IconRotate, IconTrash, IconUpload } from './icons';
import { DragHandle, SortableList } from './sortable';
import { formatBytes, useFeedback } from './ui';

const filesFrom = (e: DragEvent) => Array.from(e.dataTransfer.files).filter((f) => /^image\//.test(f.type) || /\.(webp|png|jpe?g|avif)$/i.test(f.name));

// ------------------------------------------------------------------ una foto
interface ImageSlotProps {
  media: Media | null;
  onChange: (media: Media | null) => void;
  label?: string;
  /** Proporción de la caja, p. ej. "3 / 2". */
  aspect?: string;
  /** Fondo de cuadros para ver la transparencia. */
  checker?: boolean;
  hint?: string;
  compact?: boolean;
}

/** Una foto: arrastrar y soltar o clic para elegir; muestra medidas y peso. */
export function ImageSlot({ media, onChange, label, aspect = '3 / 2', checker, hint, compact }: ImageSlotProps) {
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [over, setOver] = useState(false);
  const { toast } = useFeedback();
  const { t } = useT();

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setProgress(0);
    try {
      onChange(await uploadImage(file, setProgress));
    } catch (e) {
      toast(`${file.name}: ${errorMessage(e)}`, 'error');
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="field">
      {label && <span className="field-label">{label}</span>}
      <div
        className={`slot ${media ? 'filled' : ''} ${over ? 'drop' : ''} ${checker ? 'checker' : ''}`}
        style={{ aspectRatio: aspect, minHeight: compact ? 0 : undefined }}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          void upload(filesFrom(e)[0]);
        }}
      >
        {media ? (
          <>
            <img src={media.url} alt="" loading="lazy" />
            <span className="meta">
              {media.width}×{media.height} · {formatBytes(media.bytes)}
            </span>
            <div className="tools">
              <button type="button" className="btn btn-sm" onClick={() => input.current?.click()} title={t('Cambiar foto')}>
                <IconUpload />
              </button>
              <button type="button" className="btn btn-sm" onClick={() => onChange(null)} title={t('Quitar')}>
                <IconTrash />
              </button>
            </div>
          </>
        ) : (
          <button type="button" className="slot-empty" onClick={() => input.current?.click()}>
            <IconImage />
            <span>{progress !== null ? t('Subiendo…') : t('Arrastra una foto o haz clic')}</span>
          </button>
        )}
        {progress !== null && <div className="progress" style={{ width: `${Math.max(4, progress * 100)}%` }} />}
      </div>
      {hint && <div className="hint">{hint}</div>}
      <input
        ref={input}
        type="file"
        accept={ACCEPTED_IMAGES}
        hidden
        onChange={(e) => {
          void upload(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ------------------------------------------------------------------ un PDF
interface DocumentSlotProps {
  label: string;
  /** Qué se ve en el sitio mientras no hay archivo. */
  emptyHint?: string;
  media: Media | null;
  onChange: (media: Media | null) => void;
}

/** Un PDF: arrastrar y soltar o clic para elegir. Muestra el nombre y el peso, y se puede bajar para revisarlo. */
export function DocumentSlot({ label, emptyHint, media, onChange }: DocumentSlotProps) {
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [over, setOver] = useState(false);
  const { toast } = useFeedback();
  const { t } = useT();

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setProgress(0);
    try {
      onChange(await uploadPdf(file, setProgress));
    } catch (e) {
      toast(`${file.name}: ${errorMessage(e)}`, 'error');
    } finally {
      setProgress(null);
    }
  };

  return (
    <div
      className={`doc-slot ${media ? 'filled' : ''} ${over ? 'drop' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        void upload(e.dataTransfer.files[0]);
      }}
    >
      <span className="doc-ico" aria-hidden="true">
        <IconFile />
      </span>
      <div className="doc-info">
        <strong>{label}</strong>
        {progress !== null ? (
          <span className="muted">{t('Subiendo… {pct} %', { pct: Math.round(progress * 100) })}</span>
        ) : media ? (
          <span className="muted doc-name" title={media.originalName ?? undefined}>
            {media.originalName ?? t('documento.pdf')} · {formatBytes(media.bytes)}
          </span>
        ) : (
          <span className="faint">{emptyHint ?? t('Sin PDF todavía')}</span>
        )}
      </div>
      <div className="doc-tools">
        {media && (
          <a className="btn btn-sm btn-ghost btn-icon" href={media.url} download={media.originalName ?? true} title={t('Bajar el PDF para revisarlo')}>
            <IconDownload />
          </a>
        )}
        <button type="button" className="btn btn-sm" disabled={progress !== null} onClick={() => input.current?.click()}>
          <IconUpload /> {media ? t('Reemplazar') : t('Subir PDF')}
        </button>
        {media && (
          <button type="button" className="btn btn-sm btn-ghost btn-icon" title={t('Quitar')} onClick={() => onChange(null)}>
            <IconTrash />
          </button>
        )}
      </div>
      {progress !== null && <div className="progress" style={{ width: `${Math.max(4, progress * 100)}%` }} />}
      <input
        ref={input}
        type="file"
        accept={ACCEPTED_PDF}
        hidden
        onChange={(e) => {
          void upload(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ------------------------------------------------------------------ varias fotos en orden
export interface StripItem {
  uid: string;
  media: Media;
}

export const toStrip = (list: Media[]): StripItem[] => list.map((media) => ({ uid: crypto.randomUUID(), media }));

interface ImageStripProps {
  items: StripItem[];
  onChange: (items: StripItem[]) => void;
  addLabel: string;
  max?: number;
  checker?: boolean;
}

/** Varias fotos que se reordenan arrastrando. Al subir varias a la vez se ordenan por nombre (01, 02, …). */
export function ImageStrip({ items, onChange, addLabel, max = 72, checker }: ImageStripProps) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const { toast } = useFeedback();
  const { t } = useT();
  const latest = useRef(items);
  latest.current = items;

  const add = async (files: File[]) => {
    const room = max - items.length;
    if (!files.length) return;
    if (room <= 0) return toast(t('Máximo {max} fotos', { max }), 'error');
    const { media, failed } = await uploadMany(files.slice(0, room), (done, total) => setBusy(t('Subiendo {done} de {total}…', { done, total })));
    setBusy(null);
    onChange([...latest.current, ...toStrip(media)]);
    for (const f of failed) toast(`${f.name}: ${f.error}`, 'error');
  };

  return (
    <div>
      <SortableList items={items} getId={(i) => i.uid} onReorder={onChange} layout="grid" className="strip">
        {(item, i, handle) => (
          <div className="strip-item">
            <div className={`img ${checker ? 'checker' : ''}`}>
              <img src={item.media.url} alt="" loading="lazy" />
            </div>
            <div className="bar">
              <DragHandle {...handle} />
              <span className="n">{String(i + 1).padStart(2, '0')}</span>
              <button type="button" className="btn btn-ghost btn-sm btn-icon" title={t('Quitar')} onClick={() => onChange(items.filter((x) => x.uid !== item.uid))}>
                <IconTrash />
              </button>
            </div>
          </div>
        )}
      </SortableList>
      <div className="strip" style={{ marginTop: items.length ? 10 : 0 }}>
        <button
          type="button"
          className="strip-add"
          disabled={!!busy}
          onClick={() => input.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            void add(filesFrom(e));
          }}
        >
          <span>
            <IconUpload width={20} style={{ margin: '0 auto 6px', display: 'block' }} />
            {busy ?? addLabel}
          </span>
        </button>
      </div>
      <input
        ref={input}
        type="file"
        accept={ACCEPTED_IMAGES}
        multiple
        hidden
        onChange={(e) => {
          void add(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ------------------------------------------------------------------ vista previa del 360
/** Arrastra para girar la moto y comprobar el orden de las fotos. */
export function SpinPreview({ frames }: { frames: Media[] }) {
  const { t } = useT();
  const [i, setI] = useState(0);
  const drag = useRef<{ x: number; start: number } | null>(null);
  const n = frames.length;

  useEffect(() => {
    if (i >= n) setI(0);
  }, [n, i]);

  if (!n) return null;
  const onDown = (e: ReactPointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, start: i };
  };
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const width = e.currentTarget.clientWidth;
    const steps = Math.round(((e.clientX - drag.current.x) / width) * n * 1.5);
    setI((((drag.current.start - steps) % n) + n) % n);
  };

  return (
    <div className="spin" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => (drag.current = null)} title={t('Arrastra para girar')}>
      {frames.map((f, k) => (
        <img key={`${f.id}-${k}`} src={f.url} alt="" style={{ opacity: k === i ? 1 : 0 }} />
      ))}
      <span className="label">
        <IconRotate width={11} style={{ verticalAlign: '-1px' }} /> {i + 1} / {n}
      </span>
    </div>
  );
}
